import { motion, AnimatePresence } from 'framer-motion';
import type { Character } from '@/types';

interface InventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  character: Character | null;
}

export function InventoryModal({ isOpen, onClose, character }: InventoryModalProps) {
  if (!character) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-gray-800 rounded-2xl p-4 w-[90%] max-w-md max-h-[70vh] overflow-hidden border border-gray-700"
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-amber-500 flex items-center gap-2">
                <span>👝</span>
                <span>Сумка</span>
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-2xl transition-colors w-8 h-8 flex items-center justify-center"
              >
                ✕
              </button>
            </div>

            {/* Load and Coins */}
            <div className="flex flex-col gap-2 text-sm text-gray-400 mb-4 px-1">
              <div className="flex justify-between">
                <span>
                  Нагрузка: <span className="text-white">{character.load ?? 0}</span> / {character.maxLoad ?? 10}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-amber-700">{character.copper ?? 0}м</span>
                  <span className="text-gray-400">{character.silver ?? 0}с</span>
                  <span className="text-amber-400">{character.gold ?? 0}з</span>
                </span>
              </div>
              <div className="text-xs text-gray-500 text-right">
                медь / серебро / золото (1з = 10с = 100м)
              </div>
            </div>

            {/* Items List */}
            <div className="overflow-y-auto max-h-[50vh] space-y-2 pr-1">
              {character.inventory && character.inventory.length > 0 ? (
                character.inventory.map((item, index) => (
                  <div
                    key={item.id || index}
                    className="bg-gray-700/50 rounded-xl p-3 flex justify-between items-start border border-gray-600/50"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium flex items-center gap-2">
                        <span>{item.nameRu || item.name}</span>
                        {item.quantity && item.quantity > 1 && (
                          <span className="text-xs bg-amber-500/30 text-amber-400 px-1.5 py-0.5 rounded">
                            x{item.quantity}
                          </span>
                        )}
                      </div>
                      {item.tags && item.tags.length > 0 && (
                        <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-1">
                          {item.tags.map((tag, i) => (
                            <span
                              key={i}
                              className="bg-gray-600/50 px-1.5 py-0.5 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {item.damage && (
                        <div className="text-xs text-red-400 mt-1">
                          Урон: {item.damage}
                        </div>
                      )}
                      {item.armor !== undefined && item.armor > 0 && (
                        <div className="text-xs text-blue-400 mt-1">
                          Броня: +{item.armor}
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-gray-400 ml-2 whitespace-nowrap">
                      {item.weight} нагр.
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-12">
                  <span className="text-4xl block mb-2">🎒</span>
                  <p>Сумка пуста</p>
                </div>
              )}
            </div>

            {/* Footer with total weight */}
            {character.inventory && character.inventory.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-700 text-sm text-gray-400 text-center">
                Всего предметов: {character.inventory.length}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
